import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from "classnames";
import * as debounce from 'lodash.debounce';
import { translate } from "../Services/translation";
import Tooltip from "./Tooltip";

const FacetedSearchMultiSlider = ({ group, onFacetChange, isMobile = false }) => {
    const min = group.meta.minValue;
    const max = group.meta.maxValue;

    let initialMinValue = min;
    let initialMaxValue = max;

    if (group.selectedOptions[0]) {
        initialMinValue = group.selectedOptions[0]?.split("-")[0];
        initialMaxValue = group.selectedOptions[0]?.split("-")[1];
    }

    const [minVal, setMinVal] = useState(initialMinValue);
    const [maxVal, setMaxVal] = useState(initialMaxValue);

    //Todo: Rewrite this to useRef instead
    const [customInputMin, setCustomInputMin] = useState(initialMinValue);
    const [customInputMax, setCustomInputMax] = useState(initialMaxValue);

    const [errorMessage, setErrorMessage] = useState('');

    function filterChanges(minValue, maxValue) {
        onFacetChange(group, {
            id: [minValue, maxValue].join('-')
        });
    }

    function isWithinRange(value, minimumValue, maximumValue) {
        if (isNaN(value)) {
            setErrorMessage(translate('filter.errors.invalidvalue'));
            return false;
        }

        if (value > maximumValue) {
            setErrorMessage(translate('filter.errors.valuetoohigh', [maximumValue]));

            return false;
        }

        if (value < minimumValue) {
            setErrorMessage(translate('filter.errors.valuetoolow', [minimumValue]));

            return false;
        }

        setErrorMessage('');

        return true;
    }

    const handleFilter = useCallback(debounce(function (minValue, maxValue) {
        filterChanges(minValue, maxValue);
    }, 200, true), [])

    useEffect(() => {
        if (group.selectedOptions.length === 0) {
            setCustomInputMin(initialMinValue);
            setCustomInputMax(initialMaxValue);
            setMinVal(initialMinValue);
            setMaxVal(initialMaxValue);

            setCustomInputMin(initialMinValue);
            setCustomInputMax(initialMaxValue);
        }
    }, [group.selectedOptions]);

    function onKeyUpCustomInputMin(event) {
        if (event.key === 'Enter') {
            let value = +event.target.value;

            if (value > maxVal) {
                setErrorMessage(translate('filter.errors.valuetoohigh', [value]));
                return;
            }

            if (isWithinRange(value, min, maxVal)) {
                setMinVal(value || 0);
                handleFilter(value, customInputMax);
            }
        }
    }

    function onKeyUpCustomInputMax(event) {
        if (event.key === 'Enter') {
            let value = +event.target.value;

            if (value < minVal) {
                setErrorMessage(translate('filter.errors.valuetoolow', [value]));
                return;
            }

            if (isWithinRange(value, minVal, max)) {
                setMaxVal(value);
                handleFilter(customInputMin, value);
            }
        }
    }

    function onDragMin(event) {
        const value = Math.min(+event.target.value, +maxVal);
        setMinVal(value);
        setCustomInputMin(value);
        event.target.value = value.toString();
        handleFilter(value, customInputMax);
    }

    function onDragMax(event) {
        const value = Math.max(+event.target.value, +minVal);
        setMaxVal(value);
        setCustomInputMax(value);
        event.target.value = value.toString();
        handleFilter(customInputMin, value);
    }

    function handleMinChange(event) {
        const value = formatInputValue(event.target.value);
        setCustomInputMin(value);
    }

    function handleMaxChange(event) {
        const value = formatInputValue(event.target.value);
        setCustomInputMax(value);
    }

    /**
     *  Ensure value is a number, remove leading zeros
     * @param {any} input
     * @returns the input without laeding zeros. 0100 => 100
     */
    function formatInputValue(input) {
        return input.replace(/^0+(\d)/, "$1").replace(/^0+$/, "0");
    }
    function handleBlur(setValue, value) {
        setValue(value === "" ? "0" : parseInt(value, 10).toString());
    }

    function iOS() {
        return [
            'iPad Simulator',
            'iPhone Simulator',
            'iPod Simulator',
            'iPad',
            'iPhone',
            'iPod'
        ].includes(navigator.platform)
            // iPad on iOS 13 detection
            || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
    }

    return (
        <li className={isMobile ? "mobile-modal__filter-list-item" : ""}>
            {
                isMobile && <p className="mobile-modal__filter-title">
                    <span className="flex">
                        <span>{group.label}</span>
                        {group.description && <Tooltip inline={true} helpText={group.description} />}
                    </span>
                </p>
            }
            <div className="faceted-filter__multi-slider">
                <div>
                    <input
                        className="faceted-filter__multi-slider-number-input"
                        type="number"
                        value={customInputMin}
                        onChange={handleMinChange}
                        onBlur={() => handleBlur(setCustomInputMin, customInputMin)}
                        onKeyUp={onKeyUpCustomInputMin}
                    />
                    {translate(group.meta.unit)}
                </div>
                &nbsp; - &nbsp;
                <div>
                    <input
                        className="faceted-filter__multi-slider-number-input"
                        type="number"
                        value={customInputMax}
                        onChange={handleMaxChange}
                        onBlur={() => handleBlur(setCustomInputMax, customInputMax)}
                        onKeyUp={onKeyUpCustomInputMax}
                    />
                    {translate(group.meta.unit)}
                </div>
            </div>

            <p className="faceted-filter__error-message">{errorMessage || ''}</p>

            <input
                type="range"
                min={min}
                max={max}
                value={minVal}
                onChange={onDragMin}
                className={classNames("faceted-filter__multi-slider-thumb faceted-filter__multi-slider-thumb--zindex-3", {
                    "faceted-filter__multi-slider-thumb--zindex-5": minVal > max - 100
                })}
            />
            <input
                type="range"
                min={min}
                max={max}
                value={maxVal}
                onChange={onDragMax}
                className="faceted-filter__multi-slider-thumb faceted-filter__multi-slider-thumb--zindex-4"
            />
            <div className="faceted-filter__multi-slider">
                <div className="faceted-filter__multi-slider-track" />
                <div className="faceted-filter__multi-slider-range" />
            </div>

            <div className={"faceted-filter__multi-slider-text-container" + (iOS() ? " faceted-filter__multi-slider-text-container--ios" : "")}>
                <p className="faceted-filter__multi-slider-text">{translate(group.meta.unit, [min])}</p>
                <p className="faceted-filter__multi-slider-text">{translate(group.meta.unit, [max])}</p>
            </div>
        </li>
    )
}

export default FacetedSearchMultiSlider;